import { useCallback, useRef, useState, Dispatch, SetStateAction } from 'react';

/**
 * `useLoadingGuard` combines a ref-based guard with a loading state to prevent
 * duplicate async operations. The ref provides a synchronous check that
 * is immune to React's state batching, while the state drives UI indicators.
 *
 * Use this when a component has a single guarded async operation (e.g. modal submit).
 *
 * @returns [isLoading, guard] - isLoading for UI binding, guard to wrap async handlers
 */
export function useLoadingGuard(): [boolean, <T>(fn: () => Promise<T>) => Promise<T | undefined>] {
  const loadingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);

  const guard = useCallback(async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setIsLoading(true);
    try {
      return await fn();
    } finally {
      loadingRef.current = false;
      setIsLoading(false);
    }
  }, []);

  return [isLoading, guard];
}

/**
 * `useSharedLoadingGuard` provides a shared ref-based guard across multiple async handlers.
 * Each handler can optionally toggle its own loading state for UI indicators.
 *
 * Use this when a component has multiple mutually exclusive async operations
 * that should share a single lock (e.g. booking page with several case actions).
 *
 * @returns guard - a function that wraps async handlers with the shared ref guard
 */
export function useSharedLoadingGuard(): <T>(fn: () => Promise<T>, setLoadingState?: Dispatch<SetStateAction<boolean>>) => Promise<T | undefined> {
  const loadingRef = useRef(false);

  const guard = useCallback(async <T>(fn: () => Promise<T>, setLoadingState?: Dispatch<SetStateAction<boolean>>): Promise<T | undefined> => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoadingState?.(true);
    try {
      return await fn();
    } finally {
      loadingRef.current = false;
      setLoadingState?.(false);
    }
  }, []);

  return guard;
}
